import React, { useState, useEffect, useCallback } from 'react';
import { Download } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

interface AuditRow {
  id: number;
  created_at: string;
  event_type: string;
  record_id: string;
  status: string;
  risk_level: string;
  new_values: Record<string, any>;
}

type ExportFormat = 'csv' | 'pdf';

const EVENT_LABELS: Record<string, string> = {
  BD_ACTION_PROPOSED: 'Proposed',
  BD_ACTION_APPROVED: 'Approved',
  BD_ACTION_REJECTED: 'Rejected',
  BD_ACTION_EXECUTED: 'Executed',
  BD_ACTION_EXECUTED_STUB: 'Executed (stub)',
  BD_ACTION_EXECUTE_FAILED: 'Execute failed',
  BD_ACTION_CONFLICT: 'Conflict',
  BD_ACTION_EDITED: 'Edited',
};

/**
 * Every BD proposal and decision, admin-only. Used by the BD module's Activity
 * Feed tab and the Approvals page History toggle; reads and exports AuditLog.
 */
const AuditHistoryPanel: React.FC = () => {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<ExportFormat | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/bd-approvals/audit/log');
      setRows(res.data?.entries || []);
    } catch (err) {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const download = async (format: ExportFormat) => {
    setDownloading(format);
    try {
      const res = await api.get('/api/bd-approvals/audit/log', { params: { format }, responseType: 'blob' });
      const blob = new Blob([res.data], { type: format === 'pdf' ? 'application/pdf' : 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bd-approvals-audit-${Date.now()}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="mb-6 border rounded-lg overflow-hidden">
      <div className="bg-gray-50 px-4 py-2 border-b flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">History — every proposal and decision, newest first</span>
        <div className="flex items-center gap-2">
          {(['csv', 'pdf'] as const).map((f) => (
            <button
              key={f}
              onClick={() => download(f)}
              disabled={downloading !== null}
              className="text-xs px-2.5 py-1 rounded border text-gray-600 hover:bg-gray-100 disabled:opacity-50 inline-flex items-center gap-1"
            >
              <Download className="w-3.5 h-3.5" /> {downloading === f ? 'Downloading…' : `Download ${f.toUpperCase()}`}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="px-4 py-6 text-sm text-gray-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="px-4 py-6 text-sm text-gray-400">Nothing recorded yet.</div>
      ) : (
        <div className="divide-y max-h-80 overflow-y-auto">
          {rows.map((row) => (
            <div key={row.id} className="px-4 py-2 text-sm flex items-center justify-between gap-4">
              <div className="min-w-0">
                <span className="font-medium text-gray-800">{EVENT_LABELS[row.event_type] || row.event_type}</span>
                <span className="text-gray-400 ml-2">item #{row.record_id}</span>
                {row.new_values?.action_type && (
                  <span className="text-gray-400 ml-2">{row.new_values.action_type}</span>
                )}
              </div>
              <span className="text-xs text-gray-400 shrink-0">{new Date(row.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AuditHistoryPanel;
